import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { jwtDecode } from "jwt-decode";

const API_URL = process.env.REACT_APP_API_URL ;

const getAuthHeaders = () => {
  const token = localStorage.getItem("authToken");
  if (!token) throw new Error("Authentication token is missing.");
  return {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  };
};

export const fetchAdminProfile = createAsyncThunk(
  "profile/fetchAdminProfile",
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) throw new Error("No token found");
      const decoded = jwtDecode(token);
      const id = decoded.id;

      const res = await axios.get(
        `${API_URL}/admin-profile/${id}`,
        getAuthHeaders()
      );
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message || "Failed to fetch profile");
    }
  }
);

export const updatePassword = createAsyncThunk(
  "profile/updatePassword",
  async ({ oldPassword, newPassword }, { rejectWithValue }) => {
    try {
      const payload = {
        oldPassword: String(oldPassword).trim(),
        newPassword: String(newPassword).trim(),
      };
      const response = await axios.post(
        `${API_URL}/change-password`,
        payload,
        getAuthHeaders()
      );
      return response.data.message || "Password updated successfully.";
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to update password.");
    }
  }
);

export const updatePin = createAsyncThunk(
  "profile/updatePin",
  async ({ oldPin, newPin }, { rejectWithValue }) => {
    try {
      const payload = {
        oldPin: String(oldPin).trim(),
        newPin: String(newPin).trim(),
      };
      const response = await axios.put(
        `${API_URL}/update-pin`,
        payload,
        getAuthHeaders()
      );
      return response.data.message || "PIN updated successfully.";
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to update PIN.");
    }
  }
);

const profileSlice = createSlice({
  name: "profile",
  initialState: {
    loading: false,
    success: null,
    error: null,
    profileData: null,
  },
  reducers: {
    clearStatus: (state) => {
      state.success = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAdminProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdminProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.profileData = action.payload;
      })
      .addCase(fetchAdminProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updatePassword.pending, (state) => {
        state.loading = true;
        state.success = null;
        state.error = null;
      })
      .addCase(updatePassword.fulfilled, (state, action) => {
        state.loading = false;
        state.success = action.payload;
      })
      .addCase(updatePassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updatePin.pending, (state) => {
        state.loading = true;
        state.success = null;
        state.error = null;
      })
      .addCase(updatePin.fulfilled, (state, action) => {
        state.loading = false;
        state.success = action.payload;
      })
      .addCase(updatePin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearStatus } = profileSlice.actions;
export default profileSlice.reducer;
